import {
  all,
  call,
  cancel,
  fork,
  join,
  put,
  putResolve,
  race,
  select,
  take,
} from 'typed-redux-saga/macro';
import { modules } from '@veritone/glc-redux';
import {
  ROUTE_DOCUMENT_DETAILS,
  selectCurrentRoutePayload,
  selectRouteQuery,
} from '@common-modules/routing';
import { selectPendingDocument } from '../selectors';
import watermarksSaga from '@redact-modules/watermarks/sagas';
import { actionFetchWatermarks } from '@redact-modules/watermarks/actions';
import {
  OPEN_DOCUMENT,
  OPEN_DOCUMENT_SUCCESS,
  SET_ORG_CONFIG_UNAVAILABLE,
  SWITCH_DOCUMENT_STARTED,
} from '../actions';
import { waitForApplicationConfigs } from '@common-modules/app/sagas';
import { FETCH_APPLICATION_CONFIGS_SUCCESS } from '@common-modules/app/actions';
import { selectIsKnownWithoutDocumentRedaction } from '@common/user-permissions';
import { SET_COMBINED_PREVIEW } from '@redact-modules/projects/actions';
import { readProjectIfProject } from '@redact-modules/projects/services';
import type { ProjectDetails } from '@redact-modules/projects/models';
import { currentViewTdoId, REDACTED_TAB_QUERY_VALUE } from './editorUrlState';
import { TDOId } from '@common-modules/universal/models/Brands';
import { actionFetchRedactionStatuses } from '@redact-modules/redactionStatuses/actions';
import { actionCheckTdoPermissions } from '@common-modules/mediaDetails/actions/view';
// Deep import (not the statusHistory barrel): the barrel pulls the sagas,
// which import this module's actions — a cycle (VE-26730).
import { actionFetchLatestStatusRow } from '@common-modules/statusHistory/actions';
import {
  waitForRedactionStatusesSettled,
  watchRedactionStatusSettings,
} from '@redact-modules/redactionStatuses/sagas';
import {
  resolveEditorReturnTarget,
  warmEditorReturnTargetTree,
} from '@redact-modules/casemedia/sagas/caseDiscovery';
import { onFetchLexicons } from '@redact-modules/lexicons/sagas';
import { lexiconActions } from '@redact-modules/lexicons';

const {
  user: { LOGOUT },
} = modules;

/**
 * How long the editor waits for the org application config before telling the
 * reviewer it could not find out. Longer than the shared `APP_CONFIGS_WAIT_MS`
 * default on purpose — see the gate in `initDocumentEditor`.
 */
export const DOCUMENT_EDITOR_CONFIG_WAIT_MS = 15_000;

/**
 * Route entry-point saga registered on ROUTE_DOCUMENT_DETAILS in routes.ts.
 *
 * The routing system forks this when the route mounts and cancels it when the
 * route exits. On entry we:
 * 1. Read the tdoId from the route payload and resolve the file's parent
 *    request (VE-25873 — injecting it into a case-less URL, which redirects
 *    and re-runs this saga; we stop here in that case, before doing any work
 *    the re-run would repeat).
 * 2. Start the folder-view warm-up for the exit, plus the redaction-status
 *    watchers + fetch (same as initMediaDetailsPage) so a direct URL into the
 *    document editor still loads custom-status SDOs — needed for display
 *    labels and custom lock-edit policy. Also kick off the file's OLP check, so
 *    a caller without AIWARE_TDO_UPDATE gets a read-only editor rather than an
 *    edit-capable one (VE-20286), and boot the watermark library saga + fetch
 *    (VE-24693) — this route doesn't run initMainPage, so the editor's
 *    watermark panel would otherwise have no library to offer.
 * 3. Wait for that fetch to settle before OPEN_DOCUMENT, so lock-edit policy is
 *    evaluated against the org's real custom statuses rather than an empty
 *    list. If the fetch fails or times out, isRedactionStatusLockEdits falls
 *    back to failing *closed* on unrecognized statuses — the editor still
 *    opens, but a custom-status UUID is treated as locked rather than free.
 * 4. Wait for the org application config before opening, so a document is
 *    never MuPDF-license-metered for an org that does not have document
 *    redaction (VE-31015). If it never arrives, say so and park — the open
 *    resumes if a later retry lands it. NOT the TDO lock, which DocumentDetails
 *    takes from a mount effect above its own early returns and so is unaffected
 *    by anything here.
 * 5. Dispatch OPEN_DOCUMENT — the documentsSaga (always-on) handles the
 *    actual load.
 * 6. Once the document has actually OPENED, kick off the tier-1 status-history
 *    read (VE-26730). Gated on the open rather than fired alongside it, because
 *    history rows carry correction-note text and the SDO read is authorized at
 *    schema level, not per TDO — see the comment at the dispatch.
 */
/**
 * WHICH MEMBER HOSTS the session while the merged document is on screen.
 *
 * A project TDO has no original of its own, so nothing can be opened FOR it —
 * the editor's model is a document with an original on one tab and its output
 * on the other. The merged document borrows a member's session and shows the
 * set's output over it, which is what the `combined` query param used to
 * arrange from the other direction.
 *
 * THE FIRST MEMBER IN MERGE ORDER, deterministically. Any member would do for
 * the preview, and determinism is the point: the same URL must open the same
 * way twice, or a reload lands on a different document behind the same merged
 * PDF and the Documents panel highlights a different row each time.
 */
const hostMemberFor = (project: ProjectDetails): TDOId | undefined =>
  project.members[0]?.tdoId;

/**
 * Is the editor already showing what this route asks for?
 *
 * THE GUARD THAT MAKES THE URL AFFORDABLE. Naming the project in the path
 * turns what were view changes — clicking the merged row, flipping back to
 * the editor tab — into route changes, and a route change re-runs this saga:
 * `watchRouteSagasHandle` only skips when the type AND payload match, and
 * this route does not set `preserveSagaOnRouteChange`.
 *
 * ASKS `currentViewTdoId`, the same question the URL writer answers to decide
 * where to go. One of them says "the URL should name X, so navigate", the
 * other says "the URL already names X, so there is nothing to do" — and
 * sharing the answer is what stops those two drifting into a loop or a stall.
 *
 * It does NOT skip the forks; see where it is acted on. The re-entry cases
 * this route already relied on are unaffected, because every one of them
 * arrives with the session NOT yet showing the route's tdoId: a case-less
 * deep link has no session at all, and trim, "view redacted file" and a panel
 * switch all redirect to a document other than the open one.
 */
export function* routeAlreadyShowing(routeTdoId: TDOId) {
  const shown = yield* call(currentViewTdoId);
  return !!shown && shown.tdoId === String(routeTdoId);
}

export function* initDocumentEditor() {
  // NOTE: don't combine `as any` with `yield* select(...)` on the same line —
  // a postfix TS cast around the whole `yield*` expression prevents the
  // typed-redux-saga/macro from transforming it into a single `yield`, so it
  // falls through to a generator-delegate that tries to iterate the effect
  // descriptor and throws "object is not iterable" at runtime.
  const payload = yield* select(selectCurrentRoutePayload);
  const routeTdoId = (payload as { tdoId?: TDOId } | undefined)?.tdoId;
  if (!routeTdoId) {
    return;
  }
  /**
   * IS THE EDITOR ALREADY SHOWING WHAT THIS ROUTE ASKS FOR? Decided first,
   * acted on below — see the note where it is acted on for why not here.
   */
  const alreadyShowing = yield* call(routeAlreadyShowing, routeTdoId);
  const redirected = yield* resolveEditorReturnTarget(ROUTE_DOCUMENT_DETAILS);
  if (redirected) {
    return;
  }

  // Attached forks — cancelled on LOGOUT below, or with this saga on route exit.
  const tasks = yield* all([
    fork(warmEditorReturnTargetTree),
    fork(watchRedactionStatusSettings),
    // The FETCH watcher only (VE-24948) — the lexicon ADMIN watchers stay a
    // settings-route concern. Forked here for the same reason as the statuses
    // watcher above: the editor's LexiconPanel lists lexicons, and a direct
    // URL into the editor never runs initMainPage, so without this its
    // FETCH_LEXICONS dispatch would land on no listener and the panel would
    // show whatever the store happened to hold.
    fork(onFetchLexicons),
    // Watermark library (VE-24693) — this route doesn't run initMainPage, so the
    // library saga has to be started here for the editor's watermark panel.
    fork(watermarksSaga),
  ]);

  /**
   * NOTHING BELOW IS NEEDED WHEN IT IS ALREADY ON SCREEN — but the forks above
   * ARE, which is why this sits here and not at the top of the saga.
   *
   * `watchRouteSagasHandle` cancels the previous route task BEFORE spawning
   * this one, so by now the editor's watchers — the return-target warm-up,
   * the statuses watcher, the lexicon fetch and the watermark library — have
   * already come down with it. Returning before `all([...])` would leave the
   * editor running without them, which is a worse bargain than the slow
   * interaction this guard exists to avoid.
   *
   * What it DOES skip is everything that re-fetches or re-opens what is
   * already here, and `OPEN_DOCUMENT` above all: `handleOpenDocument` has no
   * idempotence guard, so a re-entry runs the MuPDF pre-pass again and
   * records a SECOND `artifex-mupdf` licence-meter event for a document the
   * reviewer merely clicked back to. Billing, not just speed.
   *
   * Parks on LOGOUT like the completed path below, because this saga has to
   * stay alive to hold the forks.
   */
  if (alreadyShowing) {
    yield* take(LOGOUT);
    yield* cancel(tasks);
    return;
  }

  /**
   * IS THE URL NAMING A PROJECT? Asked in PARALLEL with the waits below, not
   * before them.
   *
   * The route is handed a bare id and a merged document's URL names the
   * project, so this has to be answered before anything opens. Answering it
   * on the critical path would add a round trip to every cold document open,
   * for a question that is almost always "no" — so it rides alongside the
   * statuses fetch and the config wait, both of which take longer, and is
   * joined where the answer is first needed.
   */
  const projectProbe = yield* fork(function* probeProject() {
    try {
      // `putResolve` because the answer is the point — a plain `put`
      // dispatches the thunk and hands back the action, not what it read. The
      // same pattern `purgeRedactTdoSideData` uses, and for the same reason.
      return (yield* putResolve(
        readProjectIfProject({ tdoId: routeTdoId }) as never
      )) as ProjectDetails | null;
    } catch (err) {
      /**
       * THE EDITOR MUST NOT DEPEND ON THIS ANSWER.
       *
       * An uncaught throw in a forked task aborts its parent, and
       * `runRouteSaga` reports that and deliberately does not restart — so a
       * failure here would leave the reviewer on the loading screen for good.
       * `readProjectIfProject` swallows its own GraphQL failures, but the
       * dispatch around it is outside that, and the question is one the route
       * is allowed to have unanswered: `null` already means "open it as the
       * document it says it is".
       */
      console.warn('document editor: project probe failed', err);
      return null;
    }
  });

  // Fire-and-forget like the OLP check below: the panel renders an empty library
  // until it lands, so it must not delay OPEN_DOCUMENT.
  // `pickableOnly`: the editor reaches the library only through
  // `selectPickableWatermarks`, so a deleted or disabled row would be fetched,
  // decoded and discarded — artwork included.
  yield* put(
    actionFetchWatermarks({ limit: 1000, offset: 0, pickableOnly: true })
  );
  /**
   * THE LEXICON LIBRARY HAS TO BE FETCHED FROM HERE, not from whichever
   * component happens to want it (VE-24948).
   *
   * The editor HIDES its lexicon controls when the org's library holds nothing
   * applicable and nothing is applied to this document — a labelled button
   * whose panel says only that it is empty is worse than no button. That makes
   * the library an input to a rendering decision, and a fetch that rode on the
   * rendering closed the loop: nothing fetched, so the library read as empty,
   * so the control was not rendered, so it never mounted, so nothing fetched.
   * The control simply never appeared on a document with no lexicon already
   * applied.
   *
   * A component's mount effect cannot fix that, because the component whose
   * mount it is may be the thing being gated. The route can: it runs whatever
   * the panel decides. Fire-and-forget, like the watermark library above.
   *
   * It asks for the LIBRARY, and that is all the action means — every live
   * lexicon in the org. It used to also carry, invisibly, whichever view and
   * page the settings screen was last left on, so a visit to Deleted Lexicons
   * had the editor fetching the trash and hiding this button (VE-30783).
   */
  yield* put(lexiconActions.FETCH_LEXICONS());

  // VE-20286: resolve the file's OLP verdict so the document editor can open
  // read-only for a caller without AIWARE_TDO_UPDATE. The video editor asks for
  // this from the MediaDetails page; the document route has no equivalent
  // component-level hook, so it's dispatched here. The watcher
  // (watchCheckTdoPermissions) lives in the always-on initMediaDetails saga, so
  // it is listening regardless of which editor route is mounted, and the verdict
  // lands in the same slice selectRolePermissionHelper reads. Fire-and-forget:
  // the check defaults permissive on failure, so it must not delay the open.
  yield* put(actionCheckTdoPermissions({ tdoId: routeTdoId }));

  yield* put(actionFetchRedactionStatuses());
  // Block OPEN_DOCUMENT until customs are in store (or the fetch failed) so
  // lock policy is evaluated against real SDOs, not an empty customStatuses
  // list. Time-bounded, so a hung fetch delays the open rather than blocking
  // it forever.
  yield* waitForRedactionStatusesSettled();

  /**
   * DOES THIS ORG HAVE DOCUMENT REDACTION? ASK BEFORE OPENING ANYTHING.
   *
   * `enableDocumentRedaction` comes from the org application config, which is
   * fetched once boot finishes — so on a reload or a deep link the flag reads
   * false for every org until it lands. The page used to gate on that raw
   * flag and flashed "not enabled" at orgs that do have the feature
   * (VE-31015); gating on `selectIsKnownWithoutDocumentRedaction` instead
   * fixes the flash by treating "not loaded yet" as "not known to be off".
   *
   * But that only decides what to RENDER. The open itself is the expensive
   * half: OPEN_DOCUMENT runs the MuPDF pre-pass and records an
   * `artifex-mupdf` license-meter event. Dispatching it before the config
   * answers means an org without the feature is metered — and billed against
   * the Artifex contract — for a document it is not entitled to open. So the
   * question is asked HERE, where it can actually be acted on, rather than
   * after the fact in the view.
   *
   * The TDO lock is NOT covered by this. DocumentDetails acquires and
   * heartbeats it from a mount effect that sits above its own early returns,
   * so a disabled org still locks the file behind the "not enabled" notice —
   * as it did before this change, and as it does behind the new
   * config-unavailable screen. Gating that effect is the real fix and belongs
   * with the lock hardening in plans/document-editor-lock-assessment.md.
   *
   * Bounded, and NOT settling on the first failure: the retry ladder's first
   * rung lands a second later, and a caller that must not guess should wait
   * for it. Past the timeout the reviewer is told the truth — that we could
   * not find out — and the saga parks on the next success, so any later rung
   * of the ladder, or a retry they asked for themselves, resumes the open
   * where it left off. Nothing is opened on a guess in either direction.
   *
   * 15s, not the 10s default, because the wait is racing a ladder it cannot
   * see: `APPLICATION_CONFIGS_RETRY_DELAYS_MS` attempts at roughly t=1, 4, 12,
   * 32 and 62s. Giving up at 10s lands in the gap before the third rung, so a
   * config that arrives at 12s shows this screen for two seconds and then
   * replaces it — a flash of the wrong answer, which is the shape of bug this
   * ticket exists to remove. Waiting past that rung costs 5s of loader only
   * when the config is already failing, and buys the attempt most likely to
   * rescue it. A longer outage still ends here, which is the honest result.
   */
  const configWait = yield* waitForApplicationConfigs({
    settleOnFailure: false,
    timeoutMs: DOCUMENT_EDITOR_CONFIG_WAIT_MS,
  });
  if (configWait !== 'loaded') {
    yield* put(SET_ORG_CONFIG_UNAVAILABLE(true));
    // `finally`, because the route saga is CANCELLED on route exit while it is
    // parked here — a plain clear after the race would never run, and the next
    // document to mount would open onto a stale "unable to load" screen. The
    // component's CLOSE_DOCUMENT covers an exit that unmounts the page, but a
    // same-route-type navigation (back/forward between two document URLs)
    // respawns this saga WITHOUT unmounting, so that is not enough on its own.
    // redux-saga runs finally on cancellation and allows a put there.
    try {
      const { loggedOut } = yield* race({
        ok: take(FETCH_APPLICATION_CONFIGS_SUCCESS.type),
        loggedOut: take(LOGOUT),
      });
      if (loggedOut) {
        yield* cancel(tasks);
        return;
      }
    } finally {
      yield* put(SET_ORG_CONFIG_UNAVAILABLE(false));
    }
  }
  if (yield* select(selectIsKnownWithoutDocumentRedaction)) {
    // The page renders the "not enabled" notice off the same selector. Nothing
    // here has anything left to watch, so the forks come down with the answer.
    yield* cancel(tasks);
    return;
  }

  /**
   * WHAT THE URL NAMED, now that the probe has had the length of the waits
   * above to answer.
   *
   * A project resolves to a member's session with the set's output over it;
   * anything else is the document it says it is. A project with no member to
   * host it falls back to the latter — there is nothing to open, and the
   * route's own empty state says so more honestly than a loader that never
   * resolves. `MIN_MEMBERS` makes that unreachable in practice.
   */
  const project = yield* join(projectProbe);
  const hostMember = project ? hostMemberFor(project) : undefined;
  const projectTdoId = hostMember ? routeTdoId : undefined;
  const tdoId = hostMember ?? routeTdoId;
  if (projectTdoId) {
    // The OLP verdict that governs the editor is the HOST's, not the
    // project's — the session belongs to the member, and a project TDO's own
    // answer says nothing about the file on screen. Issued here rather than
    // beside the one above because the host is not known until now; the
    // route's own check has already gone out and is harmless.
    yield* put(actionCheckTdoPermissions({ tdoId }));
  }

  /**
   * RESTORE THE VIEW THE URL ASKS FOR, before the document opens.
   */
  const query = yield* select(selectRouteQuery);
  /**
   * `combined` IS THE LEGACY FORM and this is its last reader. The project
   * lives in the path now and the URL writer strips the param, so a link made
   * under the old scheme still resolves here and rewrites itself the first
   * time the writer runs. Nothing produces one any more.
   */
  const { tab, combined } = (query ?? {}) as {
    tab?: string;
    combined?: string;
  };
  /**
   * A PROJECT URL IS ALWAYS THE MERGED TAB. It names an output and nothing
   * else — the host member's own editor tab is not what was asked for, and
   * landing there would show a document the reviewer did not name.
   */
  const wantsRedactedTab =
    !!projectTdoId || tab === REDACTED_TAB_QUERY_VALUE || !!combined;
  /**
   * NOT WHEN A SWITCH IS ALREADY IN FLIGHT.
   */
  const alreadySwitching = yield* select(selectPendingDocument);
  /**
   * THE COMBINED CASE OVERRULES THAT GUARD.
   */
  if (wantsRedactedTab && (!alreadySwitching || !!combined || !!projectTdoId)) {
    yield* put(
      SWITCH_DOCUMENT_STARTED({
        tdoId,
        // The action's switching fields, filled for a case that is not a
        // switch: this is a COLD OPEN borrowing the hand-off. `documentName`
        // labels the row that is loading in the Documents panel, which is not
        // mounted yet on a reload, and `hasRedactedOutput` only decides
        // whether that row's placeholder name is the source file's — the tab
        // shows its own empty state when there is no output, which is the
        // honest answer either way.
        documentName: '',
        hasRedactedOutput: true,
        activeTab: 'redacted-file',
      })
    );
  }
  // The PATH first, then the legacy query param. Both say the same thing; the
  // path is the one that survives a member leaving the set.
  const previewProject = projectTdoId ?? (combined as TDOId | undefined);
  if (previewProject) {
    yield* put(SET_COMBINED_PREVIEW({ projectTdoId: previewProject }));
  }

  yield* put(OPEN_DOCUMENT({ tdoId }));

  // VE-26730: tier-1 status-history read — the newest row drives the
  // correction-note pop-up/icon. Best-effort and gate-aware; never blocks open.
  const opened = yield* race({
    success: take(
      (a: { type: string; payload?: { tdoId?: unknown } }) =>
        a.type === OPEN_DOCUMENT_SUCCESS.type &&
        String(a.payload?.tdoId) === String(tdoId)
    ),
    loggedOut: take(LOGOUT),
  });
  if (opened.success) {
    yield* put(actionFetchLatestStatusRow({ tdoId }));
    yield* take(LOGOUT);
  }

  yield* cancel(tasks);
}
